// Session token for API calls.
//
// Firebase ID tokens live one hour. The SDK renews them in the background, but
// that renewal can stop for good after a single non-network error. On 10/8/26
// one orders inbox tab sent an expired token for 30 minutes and every call came
// back 401 until the page was reloaded. The app had set its Authorization
// header once, inside the onIdTokenChanged listener, and never asked again.
//
// So every API call asks the SDK for the current token instead: a cached read
// while the token is valid, a silent renewal once it has expired. No reloads,
// no prompts.
//
// Sign-out is never decided here. A 401 means "retry once with a forced
// renewal"; if that still fails the call rejects like any other error. The only
// path to the login page is Firebase itself dropping the user, handled in
// context/User.tsx, which is the case that already ended in a dead tab.

let getAuth = () => null;

// context/User.tsx registers the Firebase auth instance at import time so this
// module stays free of Firebase (and testable with a fake).
export function setAuthProvider(provider) {
  getAuth = typeof provider === "function" ? provider : () => null;
}

export const authDebug = {
  expireNext: false, // next non-forced header is deliberately invalid
  log: false,
};

let inflightRenewal = null;

// Resolves to "Bearer <token>" or null (signed out, or the SDK could not answer,
// in which case the caller keeps whatever header it already has). Never throws.
export async function getAuthHeader({ force = false } = {}) {
  const user = getAuth()?.currentUser;
  if (!user) return null;

  if (authDebug.expireNext && !force) {
    authDebug.expireNext = false;
    return "Bearer expired-test-token";
  }

  try {
    let token;
    if (force) {
      // Parallel calls that all hit 401 at the same moment share one renewal.
      if (!inflightRenewal) {
        inflightRenewal = user.getIdToken(true).finally(() => {
          inflightRenewal = null;
        });
      }
      token = await inflightRenewal;
    } else {
      token = await user.getIdToken(false);
    }
    return token ? `Bearer ${token}` : null;
  } catch (e) {
    if (authDebug.log) console.warn("[auth] getIdToken failed:", e?.code || e?.message || e);
    return null;
  }
}

// Dev-only console hooks for exercising the renewal path by hand:
//   __portalAuth.expireNext()  the next API call goes out with a bad token; the
//                              API answers 401, the call renews and retries
//   __portalAuth.status()      signed-in uid plus the token's issue/expiry times
//   __portalAuth.log(true)     warn in the console when a renewal fails
//   __portalAuth.dropSession() sign out at the Firebase level only, the way the
//                              SDK does when a refresh token is rejected; the
//                              app should land on the login page with a notice
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window.__portalAuth = {
    dropSession: async () => {
      const auth = getAuth();
      if (!auth?.currentUser) return "nobody signed in";
      await auth.signOut();
      return "Firebase session dropped; the app should now show the login page";
    },
    expireNext: () => {
      authDebug.expireNext = true;
      return "next API call will send an expired token";
    },
    log: (on = true) => {
      authDebug.log = !!on;
    },
    status: async () => {
      const user = getAuth()?.currentUser;
      if (!user) return { signedIn: false };
      const r = await user.getIdTokenResult();
      return { signedIn: true, uid: user.uid, issued: r.issuedAtTime, expires: r.expirationTime };
    },
  };
}
